import React, { useState, useMemo, type JSX } from 'react';
import { ServiceProductVariant } from 'data/Service';
import { UseFormSetValue } from 'react-hook-form';
import MultiHeaderTable from 'components/UI/MultiHeaderTable';
import { SERVICE_CONSTANTS, SERVICE_FORM_FIELDS, SERVICE_PRODUCTS_LINKING_HEADERS } from 'constants/service';
import Modal from 'components/UI/Modal';
import { Main } from 'styles/themeColors';
import { orderByArrayObjects } from 'lib/Array';
import { getFieldName } from 'utils/addOn';
import { GENERAL_CONSTANTS } from 'constants/general';

interface Props {
    variants: ServiceProductVariant[];
    setValue?: UseFormSetValue<any>;
    importExportButtons: JSX.Element;
    readOnly?: boolean;
}

const ServiceProductsTable: React.FC<Props> = ({ variants, setValue, importExportButtons,readOnly }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [orderBy, setOrderBy] = useState<string>('');
    const [showConfirmModal, setShowConfirmModal] = React.useState(false);

    const sortedData = useMemo(() => {
        if (!orderBy) return variants;
        return orderByArrayObjects(variants, orderBy, order);
    }, [variants, orderBy, order]);

    const paginatedData = useMemo(() => {
        return sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [sortedData, page, rowsPerPage]);

    const handleSelectAll = (checked: boolean) => {
        setSelectedRows(checked ? variants.map((v) => v.id) : []);
    };

    const handleSelect = (id: number) => {
        setSelectedRows((prev) =>
            prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
        );
    };

    const handleSort = (column: string) => {
        setOrderBy(column);
        setOrder(orderBy === column && order === 'asc' ? 'desc' : 'asc');
    };

    const toggleModal = () => {
        if (selectedRows.length > 0) {
            setShowConfirmModal(!showConfirmModal);
        }
    }

    const handleDelete = () => {
        if (selectedRows.length > 0) {
            const filteredVariants = variants.filter((variant) => !selectedRows.includes(variant.id));
            setValue?.(SERVICE_FORM_FIELDS.VARIANTS, filteredVariants);
            // Clear selection after delete
            setSelectedRows([]);
            toggleModal();
        }
    };

    const confirmCloseModal = (
        <Modal
            showModal={showConfirmModal}
            onClose={toggleModal}
            onClick={handleDelete}
            title={SERVICE_CONSTANTS.REMOVE_SKUS}
            primaryButtonText={GENERAL_CONSTANTS.CONFIRM}
            ghostButtonText={GENERAL_CONSTANTS.BACK}
            primaryButtonColor={Main.brandDark}
            color={Main.black}
            borderColor={Main.disabled}
        >
            <p>{SERVICE_CONSTANTS.CONFIRM_DELETE_SKUS}</p>
        </Modal>
    )

    const customeHeader = () => (
        <div style={{ marginRight: 15 }}>{importExportButtons}</div>
    );

    return (
        <>
            <MultiHeaderTable
                headers={SERVICE_PRODUCTS_LINKING_HEADERS}
                data={paginatedData}
                orderBy={orderBy}
                order={order}
                selectedRows={selectedRows}
                onSort={handleSort}
                onSelectAll={handleSelectAll}
                onSelect={handleSelect}
                onDelete={toggleModal}
                totalCount={variants.length}
                currentPage={page}
                rowsPerPage={rowsPerPage}
                setPage={setPage}
                getFieldName={getFieldName}
                setRowsPerPage={setRowsPerPage}
                customHeaderComponent={customeHeader}
                readOnly={readOnly}
            />
            {confirmCloseModal}
        </>
    );
};

export default React.memo(ServiceProductsTable);
